import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import ThemeToggle from '../components/ThemeToggle';
import SiteFooter from '../components/SiteFooter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LoginPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      toast.error('All fields are required');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, formData);
      
      // Store token
      localStorage.setItem('sage_token', response.data.access_token);
      localStorage.setItem('sage_user', JSON.stringify(response.data.user));
      
      toast.success('Welcome back to SAGE');
      setTimeout(() => navigate('/dashboard'), 1000);
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Kick off the Emergent-managed Google OAuth flow. The user leaves this
  // tab, authenticates with Google, and returns to `${origin}/dashboard`
  // with a `#session_id=<opaque>` fragment. App.js intercepts that fragment
  // and exchanges it via POST /api/auth/google/session for a SAGE JWT.
  //
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleSignIn = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href =
      `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-obsidian relative overflow-hidden flex items-center justify-center px-6">
      {/* Floating theme toggle — top-right, matches landing page pattern. */}
      <div className="fixed top-5 right-5 z-30">
        <ThemeToggle size="sm" className="border border-white/14 bg-stone-950/40 backdrop-blur-sm rounded-sm" />
      </div>
      {/* Noise Overlay */}
      <div className="overlay-noise" />
      
      {/* Background Image with Heavy Gradient */}
      <div 
        className="absolute inset-0 bg-cover bg-center auth-bg-image"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(10, 10, 10, 0.3) 0%, rgba(10, 10, 10, 0.8) 50%, rgba(10, 10, 10, 0.95) 100%), url('https://customer-assets.emergentagent.com/job_sage-ai-coach/artifacts/jw4dk25b_background_tree_on_hill_fog.jpg')`
        }}
      />
      
      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-12">
          <h1 
            className="text-5xl md:text-6xl font-light tracking-tight text-zinc-100 mb-4"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
            data-testid="login-title"
          >
            Return to SAGE
          </h1>
          <p className="text-sm font-mono uppercase tracking-widest text-zinc-400">
            Access Your Wisdom
          </p>
        </div>

        {/* Google Sign-In — primary quick path (Emergent-managed OAuth).
            Wrapped in the threshold-button aesthetic so it visually belongs to
            the landing, but keeps the multicolor Google G intact for brand
            compliance. The G reads as a small logo mark inside the frame
            rather than the whole button being a Google product tile. */}
        <button
          type="button"
          onClick={handleGoogleSignIn}
          disabled={loading}
          className="threshold-button w-full rounded-sm px-8 py-5 text-lg md:text-xl mb-6 disabled:opacity-60 disabled:cursor-not-allowed"
          data-testid="google-signin-button"
          aria-label="Continue with Google"
        >
          {/* Google 'G' mark — inline SVG to avoid any external CDN dependency */}
          <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          <span>Continue with Google</span>
          <span className="threshold-button-arrow" aria-hidden="true">→</span>
        </button>

        {/* Visual separator between the Google button (primary quick path)
            and the email/password form (long-standing accounts). */}
        <div className="flex items-center gap-3 mb-6" aria-hidden="true">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">or</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-zinc-400 mb-2 block">
              Email
            </label>
            <Input
              type="email"
              placeholder="your@email.com"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              className="bg-transparent border-b border-white/20 focus:border-white text-white placeholder:text-zinc-500 px-0 py-4 rounded-none focus:ring-0 w-full font-light"
              data-testid="login-email-input"
              disabled={loading}
            />
          </div>

          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-zinc-400 mb-2 block">
              Password
            </label>
            <Input
              type="password"
              placeholder="Your password"
              value={formData.password}
              onChange={(e) => setFormData({...formData, password: e.target.value})}
              className="bg-transparent border-b border-white/20 focus:border-white text-white placeholder:text-zinc-500 px-0 py-4 rounded-none focus:ring-0 w-full font-light"
              data-testid="login-password-input"
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            className="threshold-button w-full rounded-sm px-8 py-5 text-lg md:text-xl mt-8 disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="login-submit-button"
            disabled={loading}
            aria-label="Enter SAGE"
          >
            <span>{loading ? 'Entering…' : 'Enter SAGE'}</span>
            {!loading && (
              <span className="threshold-button-arrow" aria-hidden="true">→</span>
            )}
          </button>
        </form>

        <div className="mt-8 text-center space-y-4">
          <Link 
            to="/" 
            className="text-xs text-zinc-500 hover:text-zinc-500 transition-colors block"
          >
            ← Back to Landing
          </Link>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
};

export default LoginPage;